// Sacrifice-cost draw helpers. Pure. No React, no Three.js.
//
// ── Codex (Sacrifice) ──
//   "To sacrifice a card, put it into its owner's cemetery. A sacrifice
//    is not destruction, so it bypasses 'can't be destroyed' / Ward."
//
// Two cards share this primitive:
//   • Cauldron Crones (Minion). "Genesis → You may sacrifice another
//     minion here to draw a spell." The sacrifice victim is a board
//     minion sharing the source's cell (exclude_self); the pick reuses
//     the standard target-pick halo flow. Optional ("may").
//   • Wiccan Tools (Artifact). "Sacrifice → Draw a spell." The
//     victim is the source itself; no pick.
//
// GameBoard owns the imperative sacrifice (sendCardToPile) + the draw +
// the halo pick orchestration. This module owns the read-only board
// query (which minions are legal sacrifice victims) so the gate that
// hides an unpayable activated cost and the pick share one source of
// truth.

import { countsAsMinion } from '../../core/typeline';

/**
 * Every minion sharing `source`'s cell that may be sacrificed for the
 * `here`-scope sacrifice cost. Mirrors `pickSacrificeHere` (Altar of
 * Malachai) but returns the full list rather than the first match, so a
 * multi-minion stack offers a real choice. Same-side only (you sacrifice
 * YOUR minions); the source is excluded.
 *
 * @param meshes     host.meshes Map
 * @param cellAt     (x, z) => { col, row } | null
 * @param source     the sacrificing card instance
 * @returns cardInstance[] of legal sacrifice victims
 */
export function sacrificeVictimsHere(meshes, cellAt, source) {
  const sourceMesh = meshes.get(source.id);
  if (!sourceMesh) return [];
  const sCell = cellAt(sourceMesh.position.x, sourceMesh.position.z);
  if (!sCell) return [];
  const out = [];
  for (const [, mesh] of meshes) {
    const c = mesh?.userData?.cardInstance;
    if (!c || c.id === source.id) continue;
    if (!countsAsMinion(c)) continue;
    if (!!c.rotated !== !!source.rotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== sCell.col || cell.row !== sCell.row) continue;
    out.push(c);
  }
  return out;
}
